import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Shield,
    Ban,
    Users,
    Lock,
    Unlock,
    Plus,
    Trash2,
    Clock,
    Smartphone,
    Globe,
    AlertTriangle
} from 'lucide-react';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import Modal from '../components/Modal';
import {
    getPlatformConfig,
    toggleSignupsPaused,
    getBlockedIPs,
    blockIP,
    unblockIP,
    getSessionStats
} from '../utils/api';
import toast from 'react-hot-toast';

export default function SecurityConfig() {
    const queryClient = useQueryClient();
    const [showBlockIPModal, setShowBlockIPModal] = useState(false);
    const [newBlockIP, setNewBlockIP] = useState({
        ipAddress: '',
        reason: '',
        blockType: 'permanent',
        expiresInDays: 7
    });
    const [signupMessage, setSignupMessage] = useState('');

    // Fetch platform config
    const { data: config, isLoading: configLoading } = useQuery({
        queryKey: ['platformConfig'],
        queryFn: getPlatformConfig,
        staleTime: 30000,
    });

    // Fetch blocked IPs
    const { data: blockedIPsData, isLoading: ipsLoading } = useQuery({
        queryKey: ['blockedIPs'],
        queryFn: () => getBlockedIPs({ limit: 50 }),
        staleTime: 30000,
    });

    // Fetch session stats
    const { data: sessionStats, isLoading: statsLoading } = useQuery({
        queryKey: ['sessionStats'],
        queryFn: getSessionStats,
        staleTime: 60000,
    });

    // Update signup message when config loads
    useEffect(() => {
        if (config?.data?.signups_paused_message) {
            setSignupMessage(config.data.signups_paused_message);
        }
    }, [config]);

    // Toggle signups mutation
    const toggleSignupsMutation = useMutation({
        mutationFn: ({ paused, message }) => toggleSignupsPaused(paused, message),
        onSuccess: () => {
            queryClient.invalidateQueries(['platformConfig']);
            toast.success(config?.data?.signups_paused ? 'Signups enabled' : 'Signups paused');
        },
        onError: (error) => {
            toast.error(error.message);
        },
    });

    // Block IP mutation
    const blockIPMutation = useMutation({
        mutationFn: blockIP,
        onSuccess: () => {
            queryClient.invalidateQueries(['blockedIPs']);
            toast.success('IP address blocked');
            setShowBlockIPModal(false);
            setNewBlockIP({ ipAddress: '', reason: '', blockType: 'permanent', expiresInDays: 7 });
        },
        onError: (error) => {
            toast.error(error.message);
        },
    });

    // Unblock IP mutation
    const unblockIPMutation = useMutation({
        mutationFn: unblockIP,
        onSuccess: () => {
            queryClient.invalidateQueries(['blockedIPs']);
            toast.success('IP address unblocked');
        },
        onError: (error) => {
            toast.error(error.message);
        },
    });

    const handleToggleSignups = () => {
        const isPaused = config?.data?.signups_paused;
        toggleSignupsMutation.mutate({
            paused: !isPaused,
            message: signupMessage
        });
    };

    const handleBlockIP = () => {
        if (!newBlockIP.ipAddress || !newBlockIP.reason) {
            toast.error('IP address and reason are required');
            return;
        }

        const expiresAt = newBlockIP.blockType === 'temporary'
            ? Date.now() + (newBlockIP.expiresInDays * 24 * 60 * 60 * 1000)
            : undefined;

        blockIPMutation.mutate({
            ipAddress: newBlockIP.ipAddress,
            reason: newBlockIP.reason,
            blockType: newBlockIP.blockType,
            expiresAt
        });
    };

    const signupsPaused = config?.data?.signups_paused;
    const blockedIPs = blockedIPsData?.data || [];
    const stats = sessionStats?.data || {};

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold mb-2">Security & Access Control</h1>
                <p className="text-muted-light dark:text-muted-dark">
                    Manage platform security settings, IP blocking, and user access
                </p>
            </div>

            {/* Signup Control */}
            <Card title="Signup Control" icon={<Lock size={20} />}>
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="font-medium">
                                Signups are currently{' '}
                                <span className={signupsPaused ? 'text-red-500' : 'text-green-500'}>
                                    {signupsPaused ? 'PAUSED' : 'OPEN'}
                                </span>
                            </p>
                            <p className="text-sm text-muted-light dark:text-muted-dark">
                                {signupsPaused
                                    ? 'New users cannot create accounts'
                                    : 'New users can create accounts normally'}
                            </p>
                        </div>
                        <Button
                            variant={signupsPaused ? 'primary' : 'danger'}
                            onClick={handleToggleSignups}
                            loading={toggleSignupsMutation.isPending}
                        >
                            {signupsPaused ? (
                                <>
                                    <Unlock size={18} />
                                    Enable Signups
                                </>
                            ) : (
                                <>
                                    <Lock size={18} />
                                    Pause Signups
                                </>
                            )}
                        </Button>
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-2">
                            Pause Message (shown to users when signups are paused)
                        </label>
                        <textarea
                            value={signupMessage}
                            onChange={(e) => setSignupMessage(e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            rows={2}
                            placeholder="We're currently not accepting new signups. Please check back later!"
                        />
                    </div>
                </div>
            </Card>

            {/* Session Statistics */}
            <Card title="Active Sessions" icon={<Users size={20} />}>
                {statsLoading ? (
                    <div className="animate-pulse space-y-2">
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                            <div className="text-2xl font-bold text-blue-600">{stats.totalActive || 0}</div>
                            <div className="text-sm text-muted-light dark:text-muted-dark">Total Active</div>
                        </div>
                        <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <div className="text-2xl font-bold">{stats.iosActive || 0}</div>
                            <div className="text-sm text-muted-light dark:text-muted-dark flex items-center justify-center gap-1">
                                <Smartphone size={14} /> iOS
                            </div>
                        </div>
                        <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                            <div className="text-2xl font-bold text-green-600">{stats.androidActive || 0}</div>
                            <div className="text-sm text-muted-light dark:text-muted-dark flex items-center justify-center gap-1">
                                <Smartphone size={14} /> Android
                            </div>
                        </div>
                        <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                            <div className="text-2xl font-bold text-purple-600">{stats.activeInLast24h || 0}</div>
                            <div className="text-sm text-muted-light dark:text-muted-dark flex items-center justify-center gap-1">
                                <Clock size={14} /> Last 24h
                            </div>
                        </div>
                    </div>
                )}

                {stats.versionBreakdown && Object.keys(stats.versionBreakdown).length > 0 && (
                    <div className="mt-4">
                        <h4 className="text-sm font-medium mb-2">App Versions</h4>
                        <div className="flex flex-wrap gap-2">
                            {Object.entries(stats.versionBreakdown).map(([version, count]) => (
                                <span
                                    key={version}
                                    className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm"
                                >
                                    {version}: {count}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </Card>

            {/* IP Blocking */}
            <Card
                title="IP Blocking"
                icon={<Ban size={20} />}
                action={
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setShowBlockIPModal(true)}
                    >
                        <Plus size={16} />
                        Block IP
                    </Button>
                }
            >
                {ipsLoading ? (
                    <div className="animate-pulse space-y-2">
                        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
                        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    </div>
                ) : blockedIPs.length === 0 ? (
                    <div className="text-center py-8 text-muted-light dark:text-muted-dark">
                        <Globe size={48} className="mx-auto mb-2 opacity-50" />
                        <p>No blocked IP addresses</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {blockedIPs.map((block) => (
                            <div
                                key={block._id}
                                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`w-2 h-2 rounded-full ${block.isActive ? 'bg-red-500' : 'bg-gray-400'}`} />
                                    <div>
                                        <div className="font-mono text-sm font-medium">{block.ipAddress}</div>
                                        <div className="text-xs text-muted-light dark:text-muted-dark">
                                            {block.reason} • {block.blockType}
                                            {block.expiresAt && (
                                                <span className="ml-1">
                                                    (expires {new Date(block.expiresAt).toLocaleDateString()})
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => unblockIPMutation.mutate(block.ipAddress)}
                                    loading={unblockIPMutation.isPending}
                                >
                                    <Trash2 size={16} />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {/* Block IP Modal */}
            <Modal
                isOpen={showBlockIPModal}
                onClose={() => setShowBlockIPModal(false)}
                title="Block IP Address"
                footer={
                    <>
                        <Button
                            variant="secondary"
                            onClick={() => setShowBlockIPModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleBlockIP}
                            loading={blockIPMutation.isPending}
                        >
                            <Ban size={18} />
                            Block IP
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Input
                        label="IP Address"
                        value={newBlockIP.ipAddress}
                        onChange={(e) => setNewBlockIP({ ...newBlockIP, ipAddress: e.target.value })}
                        placeholder="192.168.1.1"
                    />
                    <Input
                        label="Reason"
                        value={newBlockIP.reason}
                        onChange={(e) => setNewBlockIP({ ...newBlockIP, reason: e.target.value })}
                        placeholder="Spam, abuse, etc."
                    />
                    <div>
                        <label className="block text-sm font-medium mb-2">Block Type</label>
                        <select
                            value={newBlockIP.blockType}
                            onChange={(e) => setNewBlockIP({ ...newBlockIP, blockType: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="permanent">Permanent</option>
                            <option value="temporary">Temporary</option>
                        </select>
                    </div>
                    {newBlockIP.blockType === 'temporary' && (
                        <Input
                            label="Expires in (days)"
                            type="number"
                            value={newBlockIP.expiresInDays}
                            onChange={(e) => setNewBlockIP({ ...newBlockIP, expiresInDays: parseInt(e.target.value) || 7 })}
                            min={1}
                            max={365}
                        />
                    )}
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                        <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
                            <AlertTriangle size={18} />
                            <span className="text-sm font-medium">Warning</span>
                        </div>
                        <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                            Blocking an IP will immediately prevent all requests from this address.
                            Be careful not to block legitimate users.
                        </p>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

